import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import SortIcon from '@mui/icons-material/Sort';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, {useState, useCallback, useEffect} from 'react';

import {apiFetch, clearApiCache} from './api';
import AdminPanel from './AdminPanel';
import BurgerMenu from './BurgerMenu';
import {exportEmailList, exportTournamentRegistration} from './csvUtils';
import InstallPWA from './InstallPWA';
import PlayerDetails from './PlayerDetails';
import PlayerList from './PlayerList';
import {PlayerListProvider, usePlayerList} from './PlayerListContext';
import TournamentDetails from './TournamentDetails';
import TournamentList from './TournamentList';
import TournamentSeries from './TournamentSeries';
import {TournamentListProvider, useTournamentList} from './TournamentListContext';
import TournamentListSearchBar from './TournamentListSearchBar';
import PlayerListSearchBar from './PlayerListSearchBar';
import { StaleDataBanner } from './StaleDataIndicator';
import { UpdateNotifier } from './UpdateNotifier';
import UserProfile from './UserProfile';

// Navigation types
const NAV_TYPES = {
  PLAYER_LIST: 'player_list',
  TOURNAMENT_LIST: 'tournament_list',
  TOURNAMENT_SERIES: 'tournament_series',
  PLAYER_DETAIL: 'player_detail',
  TOURNAMENT_DETAIL: 'tournament_detail',
  USER_PROFILE: 'user_profile',
  ADMIN_PANEL: 'admin_panel'
};

// Navigation stack hook
function useNavigationStack(initialStack = []) {
  const [stack, setStack] = useState(initialStack);

  const push = useCallback((navObject) => {
    setStack(prev => [...prev, { ...navObject, id: Date.now() + Math.random() }]);
  }, []);

  const pop = useCallback(() => {
    setStack(prev => prev.slice(0, -1));
  }, []);

  const replace = useCallback((navObject) => {
    setStack(prev => [...prev.slice(0, -1), { ...navObject, id: Date.now() + Math.random() }]);
  }, []);

  const clear = useCallback(() => {
    setStack([]);
  }, []);

  const current = stack[stack.length - 1] || null;
  const canGoBack = stack.length > 1;

  return {
    stack,
    current,
    canGoBack,
    push,
    pop,
    replace,
    clear
  };
}

// Navigation renderer component
function NavigationRenderer({ navObject, onNavigate, onPlayerUpdated, onTournamentUpdate, onTournamentDelete, isAdmin, players, tournaments, activeOnly, setActiveOnly, reloadPlayers, reloadTournaments, playersLoading, sortBy, setSortBy, filteredPlayers, user, onBack }) {
  if (!navObject) return null;

  const { type, data } = navObject;

  switch (type) {
    case NAV_TYPES.PLAYER_LIST:
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}>
          {/* Fixed search bar */}
          <Box sx={{
            flexShrink: 0,
            px: 1, py: 0.5,
            background: '#fafafa',
            borderBottom: '1px solid #eee',
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}>
            <Box sx={{ mb: 1 }}>
              <PlayerListSearchBar />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="primary"
                onClick={() => exportEmailList(filteredPlayers)}
                size="small"
                title="Export E-Mail Liste"
                sx={{ p: 0.5 }}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="primary"
                onClick={() => exportTournamentRegistration(filteredPlayers)}
                size="small"
                title="Export Turnieranmeldung"
                sx={{ p: 0.5 }}
              >
                <EmojiEventsIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="primary"
                onClick={() => setSortBy(sortBy === 'name' ? 'elo' : 'name')}
                size="small"
                title={`Sort by ${sortBy === 'name' ? 'ELO' : 'Name'}`}
                sx={{ p: 0.5 }}
              >
                {sortBy === 'name' ? (
                  <SortByAlphaIcon fontSize="small" />
                ) : (
                  <SortIcon fontSize="small" />
                )}
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                <FilterListIcon fontSize="small" color="action" />
                <Switch
                  edge='end'
                  checked={activeOnly}
                  onChange={(_, checked) => setActiveOnly(checked)}
                  color='primary'
                  size='small'
                />
              </Box>
            </Box>
          </Box>
          
          {/* Scrollable content area */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            px: 1,
            py: 0.5
          }}>
            {playersLoading ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px'
              }}>
                <CircularProgress />
              </Box>
            ) : (
              <PlayerList
                players={players}
                onPlayerClick={(player) => onNavigate({
                  type: NAV_TYPES.PLAYER_DETAIL,
                  data: { player }
                })}
                showSearchBar={false}
              />
            )}
          </Box>
        </Box>
      );

    case NAV_TYPES.TOURNAMENT_LIST:
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}>
          {/* Fixed search bar */}
          <Box sx={{
            flexShrink: 0,
            px: 1, py: 0.5,
            background: '#fafafa',
            borderBottom: '1px solid #eee',
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}>
            <Box sx={{ mb: 1 }}>
              <TournamentListSearchBar />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="primary"
                onClick={reloadTournaments}
                size="small"
                title="Turniere neu laden"
                sx={{ p: 0.5 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {/* Scrollable content area */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            px: 1,
            py: 0.5
          }}>
            <TournamentList
              tournaments={tournaments}
              onTournamentClick={(tournament) => onNavigate({
                type: NAV_TYPES.TOURNAMENT_DETAIL,
                data: { tournament }
              })}
            />
          </Box>
        </Box>
      );

    case NAV_TYPES.PLAYER_DETAIL:
      return (
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          <PlayerDetails
            player={data.player}
            onPlayerUpdated={onPlayerUpdated}
            onTournamentClick={(tournament) => onNavigate({
              type: NAV_TYPES.TOURNAMENT_DETAIL,
              data: { tournament }
            })}
          />
        </Box>
      );

    case NAV_TYPES.TOURNAMENT_DETAIL:
      return (
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          <TournamentDetails
            tournament={data.tournament}
            onPlayerClick={(player, tournament) => onNavigate({
              type: NAV_TYPES.PLAYER_DETAIL,
              data: { player, fromTournament: tournament }
            })}
            onDelete={onTournamentDelete}
            onUpdate={onTournamentUpdate}
          />
        </Box>
      );

    case NAV_TYPES.USER_PROFILE:
      return (
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          <UserProfile user={user} onBack={onBack} />
        </Box>
      );

    case NAV_TYPES.ADMIN_PANEL:
      return (
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          <AdminPanel 
            reloadPlayers={reloadPlayers} 
            reloadTournaments={reloadTournaments}
          />
        </Box>
      );

    case NAV_TYPES.TOURNAMENT_SERIES:
      return (
        <Box sx={{ flex: 1, overflowY: 'auto', height: '100%' }}>
          <TournamentSeries />
        </Box>
      );

    default:
      return null;
  }
}

function MainWindowContent({user}) {
  const isAdmin = user && user.admin;
  const isTabletOrLarger = useMediaQuery('(min-width: 768px)');
  const {players, reloadPlayers, updatePlayer, activeOnly, setActiveOnly, inputValue, setInputValue, searchTags, setSearchTags, hasStaleData: hasStalePlayerData, loading: playersLoading, sortBy, setSortBy, filteredPlayers} = usePlayerList();
  const {tournaments, reloadTournaments, hasStaleData: hasStaleTournamentData, searchInput, setSearchInput} = useTournamentList();

  // Initialize navigation stack with player list
  const navigation = useNavigationStack([{
    type: NAV_TYPES.PLAYER_LIST,
    data: {}
  }]);

  // Handle navigation
  const handleNavigate = useCallback((navObject) => {
    navigation.push(navObject);
  }, [navigation]);

  const handleBack = useCallback(() => {
    navigation.pop();
  }, [navigation]);

  const handleTournamentDelete = useCallback((tournament_id) => {
    // If we're currently viewing the deleted tournament, go back
    if (navigation.current?.type === NAV_TYPES.TOURNAMENT_DETAIL &&
        navigation.current?.data?.tournament?.id === tournament_id) {
      navigation.pop();
    }
  }, [navigation]);

  const handleTournamentUpdate = useCallback((updatedTournament) => {
    // Update the tournament in the current navigation object if it's a tournament detail
    if (navigation.current?.type === NAV_TYPES.TOURNAMENT_DETAIL) {
      navigation.replace({
        ...navigation.current,
        data: { ...navigation.current.data, tournament: updatedTournament }
      });
    }
    reloadTournaments();
  }, [navigation, reloadTournaments]);

  const handleLogout = async () => {
    try {
      await apiFetch('/logout', {method: 'POST'});
      // Clear cache on logout
      await clearApiCache();
      window.location.reload();
    } catch (err) {
      // Optionally show an error message
    }
  };

  // Determine current tab based on navigation
  let currentTab = 0;
  if (navigation.current?.type === NAV_TYPES.TOURNAMENT_LIST ||
      navigation.current?.type === NAV_TYPES.TOURNAMENT_DETAIL) {
    currentTab = 1;
  } else if (navigation.current?.type === NAV_TYPES.TOURNAMENT_SERIES) {
    currentTab = 2;
  } else if (navigation.current?.type === NAV_TYPES.ADMIN_PANEL) {
    currentTab = 3;
  }

  // Handle tab changes
  const handleTabChange = useCallback((_, newTab) => {
    // Clear navigation stack and search state, then start fresh with the new tab's list
    navigation.clear();
    
    // Reset search state when switching tabs to provide a fresh start
    if (newTab === 0) {
      // For player tab, reset player search state
      setActiveOnly(true); // Reset to active only
      setInputValue(''); // Clear search input
      setSearchTags([]); // Clear tag filters
      navigation.push({ type: NAV_TYPES.PLAYER_LIST, data: {} });
    } else if (newTab === 1) {
      // For tournament tab, reset tournament search state
      setSearchInput(''); // Clear tournament search input
      navigation.push({ type: NAV_TYPES.TOURNAMENT_LIST, data: {} });
    } else if (newTab === 2) {
      // For tournament series tab
      navigation.push({ type: NAV_TYPES.TOURNAMENT_SERIES, data: {} });
    } else if (newTab === 3) {
      // For admin panel tab
      navigation.push({ type: NAV_TYPES.ADMIN_PANEL, data: {} });
    }
  }, [navigation, setActiveOnly, setInputValue, setSearchTags, setSearchInput]);

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AppBar 
        position='fixed' 
        sx={{ 
          width: '100%',
          paddingTop: 'env(safe-area-inset-top)',
          '@supports (padding-top: env(safe-area-inset-top))': {
            paddingTop: 'env(safe-area-inset-top)',
          }
        }}
      >
        <Toolbar>
          {navigation.canGoBack && (
            <IconButton edge='start' color='inherit' onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{ minHeight: 48 }}
            textColor='inherit'
            indicatorColor='secondary'
            slotProps={{
              indicator: { style: { height: 3 } }
            }}
          >
            <Tab 
              icon={<PeopleIcon />} 
              sx={{ minWidth: 48 }} 
              onClick={() => handleTabChange(null, 0)}
            />
            <Tab 
              icon={<EmojiEventsIcon />} 
              sx={{ minWidth: 48 }} 
              onClick={() => handleTabChange(null, 1)}
            />
            <Tab 
              icon={<MergeTypeIcon />} 
              sx={{ minWidth: 48 }} 
              onClick={() => handleTabChange(null, 2)}
              title="Turnierserie"
            />
            {isAdmin && (
              <Tab 
                icon={<SettingsIcon />} 
                sx={{ minWidth: 48 }} 
                onClick={() => handleTabChange(null, 3)}
                title="Administration"
              />
            )}
          </Tabs>
          <Box sx={{ ml: 'auto' }}>
            <BurgerMenu 
              onLogout={handleLogout} 
              isAdmin={isAdmin} 
              onProfile={() => navigation.push({ type: NAV_TYPES.USER_PROFILE, data: {} })}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Show stale data banner if offline and data is stale */}
      <StaleDataBanner data={{ 
        _isStale: hasStalePlayerData || hasStaleTournamentData 
      }} />

      <Box sx={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        marginTop: 'calc(64px + env(safe-area-inset-top))', // Standard AppBar height + safe area
        '@media (max-width: 600px)': {
          marginTop: 'calc(56px + env(safe-area-inset-top))' // Mobile AppBar height + safe area
        }
      }}>
        {/* Master/Detail layout for tablet+ */}
        {isTabletOrLarger && 
         navigation.current?.type !== NAV_TYPES.USER_PROFILE && 
         navigation.current?.type !== NAV_TYPES.USER_MANAGEMENT && 
         navigation.current?.type !== NAV_TYPES.TOURNAMENT_SERIES ? (
          <>
            {/* Master panel */}
            <Box sx={{
              width: { 
                md: 420,  // Increased for tablet landscape
                lg: 480,  // Increased for small desktops
                xl: 520   // Increased for large desktops
              },
              minWidth: 320,
              maxWidth: { md: '35%', lg: '40%', xl: '45%' }, // Percentage-based max width
              borderRight: '1px solid #ddd',
              height: '100%',
              overflowY: 'auto'
            }}>
              <NavigationRenderer
                navObject={
                  navigation.current?.type === NAV_TYPES.PLAYER_DETAIL ?
                    { type: NAV_TYPES.PLAYER_LIST, data: {} } :
                  navigation.current?.type === NAV_TYPES.TOURNAMENT_DETAIL ?
                    { type: NAV_TYPES.TOURNAMENT_LIST, data: {} } :
                  navigation.current
                }
                onNavigate={handleNavigate}
                onPlayerUpdated={updatePlayer}
                onTournamentUpdate={handleTournamentUpdate}
                onTournamentDelete={handleTournamentDelete}
                isAdmin={isAdmin}
                players={players}
                tournaments={tournaments}
                activeOnly={activeOnly}
                setActiveOnly={setActiveOnly}
                reloadPlayers={reloadPlayers}
                reloadTournaments={reloadTournaments}
                playersLoading={playersLoading}
                sortBy={sortBy}
                setSortBy={setSortBy}
                filteredPlayers={filteredPlayers}
                user={user}
                onBack={handleBack}
              />
            </Box>

            {/* Detail panel */}
            {(navigation.current?.type === NAV_TYPES.PLAYER_DETAIL ||
              navigation.current?.type === NAV_TYPES.TOURNAMENT_DETAIL) && (
              <NavigationRenderer
                navObject={navigation.current}
                onNavigate={handleNavigate}
                onPlayerUpdated={updatePlayer}
                onTournamentUpdate={handleTournamentUpdate}
                onTournamentDelete={handleTournamentDelete}
                isAdmin={isAdmin}
                players={players}
                tournaments={tournaments}
                activeOnly={activeOnly}
                setActiveOnly={setActiveOnly}
                reloadPlayers={reloadPlayers}
                reloadTournaments={reloadTournaments}
                playersLoading={playersLoading}
                filteredPlayers={filteredPlayers}
                user={user}
                onBack={handleBack}
              />
            )}
          </>
        ) : (
          /* Mobile layout - show current navigation object */
          <NavigationRenderer
            navObject={navigation.current}
            onNavigate={handleNavigate}
            onPlayerUpdated={updatePlayer}
            onTournamentUpdate={handleTournamentUpdate}
            onTournamentDelete={handleTournamentDelete}
            isAdmin={isAdmin}
            players={players}
            tournaments={tournaments}
            activeOnly={activeOnly}
            setActiveOnly={setActiveOnly}
            reloadPlayers={reloadPlayers}
            reloadTournaments={reloadTournaments}
            playersLoading={playersLoading}
            sortBy={sortBy}
            setSortBy={setSortBy}
            filteredPlayers={filteredPlayers}
            user={user}
            onBack={handleBack}
          />
        )}
      </Box>
      <InstallPWA />
      <UpdateNotifier />
    </Box>
  );
}

function MainWindow({ user }) {
  return (
    <PlayerListProvider>
      <TournamentListProvider>
        <MainWindowContent user={user} />
      </TournamentListProvider>
    </PlayerListProvider>
  );
}

export default MainWindow;
